export interface LogoProps {
  /** `mark` is the monogram alone; `lockup` adds the "Sierra Bravo 3.0" wordmark. */
  variant?: 'mark' | 'lockup';
  height?: number;
  /**
   * On dark backgrounds the mark's plate becomes transparent, matching
   * assets/logo-mark-white.svg in the design system.
   */
  onDark?: boolean;
  className?: string;
}

/**
 * Inline port of assets/logo-lockup.svg and assets/logo-mark.svg.
 *
 * The source SVGs hard-code the wordmark fill, which makes them unreadable in
 * dark mode. Rendering inline lets the wordmark inherit `currentColor` instead,
 * so one component covers every surface.
 */
export function Logo({ variant = 'lockup', height = 32, onDark = false, className }: LogoProps) {
  const isLockup = variant === 'lockup';
  const width = isLockup ? (height * 222) / 40 : height;

  // `onDark` means the surface behind the mark is dark in BOTH themes — the
  // cobalt application bar and the auth panel, not "the dark theme is active".
  // So the mark must not follow the theme's text colour: currentColor resolves
  // to near-black in light mode and the shield vanishes into the cobalt.
  //
  // The strokes are white either way, matching logo-mark.svg (white on the
  // cobalt plate) and logo-mark-white.svg (white on a dark surface, no plate).
  const plate = onDark ? 'none' : 'oklch(37% 0.177 263.4)';
  const stroke = 'white';
  // Only the wordmark tracks the theme, and only when it sits on a page
  // surface; on a dark surface it is white, as in logo-lockup-white.svg.
  const wordmark = onDark ? 'white' : 'currentColor';

  return (
    <svg
      width={width}
      height={height}
      viewBox={isLockup ? '0 0 222 40' : '0 0 40 40'}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Sierra Bravo 3.0"
    >
      <path
        d="M20 3.5 L32.8 8.1 V19.6 C32.8 27.1 27.2 32.9 20 36.5 C12.8 32.9 7.2 27.1 7.2 19.6 V8.1 Z"
        fill={plate}
        stroke={stroke}
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <path d="M14.8 25.2 L25.2 14.8" stroke={stroke} strokeWidth="3" strokeLinecap="round" />
      <path
        d="M19.6 14.8 H25.2 V20.4"
        stroke={stroke}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="14.8" cy="25.2" r="2.1" fill="oklch(75% 0.15 60)" />
      {isLockup ? (
        <text
          x="48"
          y="26"
          fontFamily="Sora, sans-serif"
          fontSize="19"
          fontWeight="600"
          fill={wordmark}
        >
          Sierra Bravo
          <tspan dx="4" fontSize="21" fontWeight="600" opacity="0.65">
            3.0
          </tspan>
        </text>
      ) : null}
    </svg>
  );
}
